import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonIcon from "@mui/icons-material/Person";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";
import type { WalletData } from "../../apis/admin/wallet-apis";
import "./UnifiedWalletView.scss";
import moment from "moment";
import { strings } from "../../I18n";
import { getCurrentUser } from "../../utils/firebase";
import { modifyCurrencyToSymbolAndValue } from "../../utils/utils";
import CustomerDetails from "../customerProfile/CustomerDetails";
import CustomerTags from "../customerProfile/CustomerTags";
import CustomerTransactions from "../customerProfile/CustomerTransactions";
import CustomerTrips from "../customerProfile/CustomerTrips";

interface LegacyBalance {
  balance: number;
  currency: string;
}

interface Props {
  walletData: WalletData;
  userDetailInfo?: any;
  legacyTransactions?: any[];
  openAdjustModal?: () => void;
  currency?: string;
  onEditClick?: () => void;
  user?: any;
  legacyBalance?: LegacyBalance;
}

const UnifiedWalletView: React.FC<Props> = ({
  walletData,
  userDetailInfo,
  legacyTransactions = [],
  openAdjustModal,
  currency: currencyProp,
  onEditClick,
  user,
  legacyBalance,
}) => {
  const { unifiedWallet } = walletData;
  const [activeTab, setActiveTab] = useState(0);
  const [pendingDialogOpen, setPendingDialogOpen] = useState(false);
  const [copyToastOpen, setCopyToastOpen] = useState(false);

  const isMigratedUser = !!walletData?.migratedUser;
  const hasUnifiedWallet = unifiedWallet !== null;
  const showUnifiedWallet = isMigratedUser && hasUnifiedWallet;

  const pendingTransactions = useMemo(() => {
    if (!showUnifiedWallet) return [];
    const pending = (walletData as any)?.pendingTransactions || [];
    if (pending.length > 0) {
      return pending
        .slice()
        .sort(
          (a: any, b: any) =>
            new Date(b.creationDatetime).getTime() -
            new Date(a.creationDatetime).getTime()
        );
    }
    const transactions = walletData?.unifiedTransactions || [];
    return transactions
      .filter((txn: any) => txn.status === "PROCESSING")
      .sort(
        (a: any, b: any) =>
          new Date(b.creationDatetime).getTime() -
          new Date(a.creationDatetime).getTime()
      );
  }, [walletData, showUnifiedWallet]);

  const balance = showUnifiedWallet
    ? unifiedWallet!.amountMinor / 100
    : !isMigratedUser &&
        legacyBalance?.balance !== undefined &&
        legacyBalance?.balance !== null
      ? legacyBalance.balance / 100
      : null;
  const pendingBalance = showUnifiedWallet
    ? unifiedWallet!.pendingAmountMinor / 100
    : null;
  const currency = showUnifiedWallet
    ? unifiedWallet!.currency || currencyProp || ""
    : !isMigratedUser
      ? legacyBalance?.currency || currencyProp || ""
      : currencyProp || "";

  const handleCopyId = () => {
    if (userDetailInfo?.userId) {
      navigator.clipboard.writeText(userDetailInfo.userId.toString());
    }
  };

  const handleCopyTxnId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopyToastOpen(true);
  };

  const handleOpenPendingDialog = () => {
    setPendingDialogOpen(true);
  };

  const handleClosePendingDialog = () => {
    setPendingDialogOpen(false);
  };

  const getTimeElapsed = (datetime: string): string => {
    const now = moment();
    const created = moment(datetime);
    const diffMinutes = now.diff(created, "minutes");
    const diffHours = now.diff(created, "hours");
    const diffDays = now.diff(created, "days");

    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    }
  };

  const displayName =
    userDetailInfo?.fullName || userDetailInfo?.userName || "Unknown User";
  const email = userDetailInfo?.email;
  const phone =
    userDetailInfo?.phoneNumber ||
    userDetailInfo?.phone ||
    userDetailInfo?.phone_number;
  const status = userDetailInfo?.status;
  const emailVerified = userDetailInfo?.emailVerified;

  const renderSidebar = () => (
    <Card elevation={3} sx={{ height: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Avatar sx={{ width: 80, height: 80, mb: 2, bgcolor: "grey.400" }}>
            <PersonIcon sx={{ fontSize: 48 }} />
          </Avatar>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, textAlign: "center" }}
          >
            {displayName}
          </Typography>
          {status && (
            <Chip
              label={status}
              size="small"
              color={
                status === "ACTIVE"
                  ? "success"
                  : status === "INACTIVE"
                    ? "warning"
                    : "error"
              }
              sx={{ mt: 1 }}
            />
          )}
          {onEditClick && (
            <IconButton size="small" onClick={onEditClick} sx={{ mt: 1 }}>
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" color="textSecondary">
            {strings("descriptions.userId")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="body2">
              {userDetailInfo?.userId || "-"}
            </Typography>
            {userDetailInfo?.userId && (
              <Tooltip title="Copy ID">
                <IconButton size="small" onClick={handleCopyId}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {email && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              {strings("descriptions.email")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                {email}
              </Typography>
              {emailVerified && (
                <CheckCircleIcon fontSize="small" color="success" />
              )}
            </Box>
          </Box>
        )}

        {phone && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="textSecondary">
              {strings("descriptions.phone")}
            </Typography>
            <Typography variant="body2">{phone}</Typography>
          </Box>
        )}

        {walletData.userId && (
          <>
            <Divider sx={{ my: 2 }} />
            <CustomerTags
              userId={walletData.userId}
              adminEmail={getCurrentUser()?.email || ""}
            />
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {strings("descriptions.walletBalance")}
          </Typography>
          {!isMigratedUser && (
            <Chip
              label="Legacy"
              size="small"
              color="default"
              variant="outlined"
            />
          )}
        </Box>

        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {strings("descriptions.balance")}:
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color:
                  balance === null
                    ? "text.primary"
                    : balance >= 0
                      ? "#389e0d"
                      : "#cf1322",
                fontWeight: 600,
              }}
            >
              {balance === null
                ? "Unknown"
                : modifyCurrencyToSymbolAndValue(currency, balance)}
            </Typography>
          </Box>
          {showUnifiedWallet && pendingBalance !== null && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                {strings("descriptions.pending")}:
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2">
                  {modifyCurrencyToSymbolAndValue(currency, pendingBalance)}
                </Typography>
                {pendingBalance > 0 && (
                  <Tooltip title="View pending transactions">
                    <IconButton size="small" onClick={handleOpenPendingDialog}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {openAdjustModal && (
          <Button
            variant="outlined"
            color="primary"
            onClick={openAdjustModal}
            fullWidth={true}
            sx={{ mt: 2 }}
          >
            {strings("adjustBalance")}
          </Button>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="unified-wallet-view">
      <Grid container={true} spacing={3}>
        <Grid item={true} xs={12} md={3}>
          {renderSidebar()}
        </Grid>

        <Grid item={true} xs={12} md={9}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(_e, newValue) => setActiveTab(newValue)}
            >
              <Tab
                label={strings("descriptions.details")}
                sx={{ textTransform: "none" }}
              />
              <Tab
                label={strings("descriptions.trips")}
                sx={{ textTransform: "none" }}
              />
              <Tab
                label={strings("descriptions.transactions")}
                sx={{ textTransform: "none" }}
              />
            </Tabs>
          </Box>

          {activeTab === 0 && (
            <CustomerDetails
              user={user}
              userDetailInfo={userDetailInfo}
              walletData={walletData}
            />
          )}

          {activeTab === 1 && userDetailInfo?.userId && (
            <CustomerTrips userId={userDetailInfo.userId} />
          )}

          {activeTab === 2 && userDetailInfo?.userId && (
            <CustomerTransactions
              userId={userDetailInfo.userId}
              walletData={walletData}
              legacyTransactions={legacyTransactions}
              currency={currency}
            />
          )}
        </Grid>
      </Grid>

      <Dialog
        open={pendingDialogOpen}
        onClose={handleClosePendingDialog}
        maxWidth="sm"
        fullWidth={true}
      >
        <DialogTitle>Pending Transactions</DialogTitle>
        <DialogContent>
          {pendingTransactions.length === 0 ? (
            <Alert severity="info">No pending transactions</Alert>
          ) : (
            <List>
              {pendingTransactions.map((txn: any) => {
                const timeElapsed = getTimeElapsed(txn.creationDatetime);
                const amount = txn.amountMinor / 100;
                const isOld =
                  moment().diff(moment(txn.creationDatetime), "hours") >= 2;

                return (
                  <ListItem
                    key={txn.id}
                    sx={{
                      border: "1px solid",
                      borderColor: isOld ? "warning.main" : "divider",
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: isOld ? "warning.lighter" : "background.paper",
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body1" fontWeight={500}>
                            {txn.type === "ADD" ? "Top Up" : "Deduction"}
                          </Typography>
                          <Chip
                            label="Processing"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Amount:{" "}
                            <strong>
                              {modifyCurrencyToSymbolAndValue(currency, amount)}
                            </strong>
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Started: {timeElapsed}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            Transaction ID: {txn.id}
                            <Tooltip title="Copy transaction ID">
                              <IconButton
                                size="small"
                                onClick={() => handleCopyTxnId(txn.id)}
                                sx={{ ml: 0.5 }}
                              >
                                <AssignmentIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Pending transactions are waiting for payment provider
              confirmation. Typically complete within 1-5 minutes.
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={copyToastOpen}
        autoHideDuration={2000}
        onClose={() => setCopyToastOpen(false)}
        message="Copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </div>
  );
};

export default UnifiedWalletView;
